import { useEffect, useRef, useState } from 'react';

const flowSteps = [
  {
    id: 'intake',
    icon: 'ri-inbox-archive-line',
    label: 'Intake',
    description: 'Capture leads & requests automatically',
    color: '#7C3AED',
  },
  {
    id: 'automation',
    icon: 'ri-settings-4-line',
    label: 'Automation',
    description: 'Trigger workflows, route data, assign tasks',
    color: '#5B21B6',
  },
  {
    id: 'fulfillment',
    icon: 'ri-stack-line',
    label: 'Fulfillment',
    description: 'Deliver services, sync tools, update records',
    color: '#14B8A6',
  },
  {
    id: 'followup',
    icon: 'ri-send-plane-line',
    label: 'Follow-Up',
    description: 'Automated reminders, reviews & nurture',
    color: '#0D9488',
  },
];

const SolutionSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="solution" className="py-28 bg-white overflow-hidden">
      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-16 items-start">
          {/* Left: Text */}
          <div
            className="lg:w-5/12 lg:sticky lg:top-28"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateX(0)' : 'translateX(-24px)',
              transition: 'opacity 0.7s ease, transform 0.7s ease',
            }}
          >
            <span className="font-inter text-xs font-semibold text-brand-teal tracking-widest uppercase mb-4 block">
              The Solution
            </span>
            <h2
              className="font-space font-semibold text-brand-text leading-tight mb-6"
              style={{ fontSize: 'clamp(2rem, 3.5vw, 2.75rem)' }}
            >
              We Design Systems That{' '}
              <span className="text-brand-violet">Run Your Business</span>
            </h2>
            <p className="font-inter text-brand-steel leading-relaxed text-base mb-8">
              Workflow Wizards AI transforms fragmented operations into connected, automated systems. We design workflows that capture leads, streamline communication, automate scheduling, and improve team efficiency — so you can focus on delivering value, not managing tasks.
            </p>

            {/* Feature bullets */}
            <div className="flex flex-col gap-4">
              {[
                { icon: 'ri-links-line', text: 'Unified tools that talk to each other' },
                { icon: 'ri-timer-flash-line', text: 'Instant response to new leads and requests' },
                { icon: 'ri-bar-chart-grouped-line', text: 'Full pipeline visibility for your team' },
                { icon: 'ri-shield-check-line', text: 'Reliable, documented, and maintainable systems' },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-violet/8 flex-shrink-0">
                    <i className={`${item.icon} text-brand-violet text-sm`} />
                  </div>
                  <span className="font-inter text-sm text-brand-text">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Flow diagram */}
          <div className="lg:w-7/12">
            {/* System flow visualization */}
            <div
              className="relative rounded-2xl p-8 overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #0f0e2e 0%, #1E1B4B 60%, #2d1b69 100%)',
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(24px)',
                transition: 'opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s',
              }}
            >
              {/* Background glow */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-brand-violet/10 blur-3xl pointer-events-none" />

              <p className="font-inter text-xs font-semibold text-white/40 tracking-widest uppercase mb-8 text-center">
                Your Automated Business System
              </p>

              {/* Flow nodes */}
              <div className="relative flex flex-col gap-0">
                {flowSteps.map((step, i) => (
                  <div key={step.id}>
                    <div
                      className="flex items-start gap-5 bg-white/5 rounded-xl p-5 border border-white/10 hover:border-white/20 transition-colors duration-200"
                      style={{
                        opacity: visible ? 1 : 0,
                        transform: visible ? 'translateX(0)' : 'translateX(24px)',
                        transition: `opacity 0.6s ease ${0.3 + i * 0.15}s, transform 0.6s ease ${0.3 + i * 0.15}s`,
                      }}
                    >
                      {/* Step icon */}
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: `${step.color}25`, border: `1px solid ${step.color}40` }}
                      >
                        <i className={`${step.icon} text-lg`} style={{ color: step.color }} />
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <span
                            className="font-inter text-[10px] font-bold tracking-widest uppercase"
                            style={{ color: step.color }}
                          >
                            Step {i + 1}
                          </span>
                        </div>
                        <p className="font-space font-semibold text-white text-sm mb-1">{step.label}</p>
                        <p className="font-inter text-white/50 text-xs leading-relaxed">{step.description}</p>
                      </div>

                      {/* Status indicator */}
                      <div className="w-2 h-2 rounded-full flex-shrink-0 mt-2" style={{ background: step.color }} />
                    </div>

                    {/* Connector arrow */}
                    {i < flowSteps.length - 1 && (
                      <div className="flex items-center justify-center py-2">
                        <div className="flex flex-col items-center gap-0.5">
                          <div className="w-px h-3 bg-white/15" />
                          <i className="ri-arrow-down-s-line text-white/20 text-base" />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Output result */}
              <div
                className="mt-6 rounded-xl p-4 border flex items-center gap-4"
                style={{
                  background: 'linear-gradient(135deg, rgba(20,184,166,0.15), rgba(20,184,166,0.05))',
                  borderColor: 'rgba(20,184,166,0.3)',
                  opacity: visible ? 1 : 0,
                  transition: 'opacity 0.7s ease 0.9s',
                }}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-brand-teal/20 flex-shrink-0">
                  <i className="ri-checkbox-circle-line text-brand-teal text-lg" />
                </div>
                <div>
                  <p className="font-space font-semibold text-brand-teal text-sm">System Running</p>
                  <p className="font-inter text-white/45 text-xs">Fully automated. Zero manual intervention required.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;
